import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useContext } from 'react';

import { Button } from '@/components/ui/button.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.js';

import { EventContext } from './session-schedule-calendar-shared.js';

export interface SessionScheduleToolbarLayoutProps {
  label: string;
  view: string;
  views: readonly string[];
  viewLabels: Record<string, string>;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewChange: (view: string) => void;
}

/** Shared coach calendar toolbar UI (react-big-calendar + Schedule-X). */
export function SessionScheduleToolbarLayout({
  label,
  view,
  views,
  viewLabels,
  onPrevious,
  onNext,
  onToday,
  onViewChange,
}: SessionScheduleToolbarLayoutProps) {
  const { isLoading, toolbarLeftAddon } = useContext(EventContext);

  return (
    <div className="session-schedule-calendar-toolbar mb-2 flex min-w-0 flex-col gap-2 md:flex-row md:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 md:text-xl max-md:text-base">
        <span className="min-w-0 truncate">{label}</span>
        {toolbarLeftAddon && (
          <div className="session-schedule-calendar-toolbar-addon min-w-0 max-w-full flex-1 sm:flex-initial text-base">
            {toolbarLeftAddon}
          </div>
        )}
      </div>
      <div className="flex shrink-0 min-w-0 flex-wrap items-center justify-between gap-2 md:w-auto md:flex-nowrap md:justify-end">
        <div className="flex items-center md:pr-2">
          {isLoading && <LoaderIcon />}
          <Button
            variant="ghost"
            size="icon"
            onClick={onPrevious}
            aria-label="Previous period"
            data-testid="sessions-calendar-button-prev"
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onNext}
            aria-label="Next period"
            data-testid="sessions-calendar-button-next"
          >
            <ChevronRight />
          </Button>
          <Button variant="outline" onClick={onToday} data-testid="sessions-calendar-button-today">
            Today
          </Button>
        </div>
        <Tabs value={view} onValueChange={onViewChange} className="flex pb-0 md:ml-auto md:pb-2">
          <TabsList>
            {views.map((viewName) => (
              <TabsTrigger key={viewName} value={viewName} data-testid={`sessions-calendar-button-view-${viewName}`}>
                {viewLabels[viewName] ?? viewName}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}
